/**
 * 2. pálya — OLVASZTÓMŰ
 *
 * Térdramaturgia (C): belépő zsilip → keskeny híd egy savas medence fölött
 * (az első hely, ahol a TÉR maga is sebez) → nagy öntőcsarnok, két szintre
 * osztva → teherlift a kezelőgalériára → őrposzt a kék kulccsal (a
 * felderítés kötelező, nem opcionális) → vissza a csarnokon át → zárt
 * zsilip → reaktorterem, a pálya legnagyobb arénája → magkamra, kijárat.
 *
 * Amit ez a pálya hoz az 1.-hez képest:
 *  - **veszélyes padló**: a savas medencében folyamatosan fogy az élet,
 *    ezért a lezuhanás nem „semmi", de nem is halál — kimászható,
 *    és cserébe lőszer van odalent (kockázat/jutalom)
 *  - **függőleges harc**: a galériáról belátni a csarnokot, és fordítva
 *  - **kulcs-hurok**: a kijárat előtti ajtó visszaküld a már bejárt téren át
 *
 * A geometria C gazdája; az ENCOUNTER-elhelyezés (`things` ellenség-sorai)
 * X-é.
 */

import type { LevelDef } from '../../world/level'
import { box, v } from '../../world/level'

export const LEVEL_2: LevelDef = {
  id: 'l2',
  name: 'Olvasztómű',
  subtitle: 'Kohósor · 5. szint',
  skyColor: 0x120c08,
  fogColor: 0x1d1510,
  fogDensity: 0.0165,
  ambient: 0.38,

  rooms: [
    {
      id: 1,
      name: 'Belépő zsilip',
      poly: box(-8, -6, 8, 4),
      floorY: 0,
      ceilY: 6,
      light: 0.55,
      tex: { floor: 'floorTile', ceil: 'ceilingTech', wall: 'techPanel' },
    },
    {
      id: 2,
      name: 'Szervizfolyosó',
      poly: box(-2, 4, 2, 14),
      floorY: 0,
      ceilY: 3.6,
      light: 0.3,
      tex: { floor: 'grate', ceil: 'metalRibbed', wall: 'metalRibbed' },
    },

    // --- A savas medence és a rajta átvezető híd -----------------------------
    {
      id: 3,
      name: 'Nyugati medence',
      poly: box(-14, 14, -2, 30),
      floorY: -1.2,
      ceilY: 10,
      light: 0.34,
      hazard: 7,
      tex: { floor: 'nukage', ceil: 'ceilingTech', wall: 'metalPlate' },
    },
    {
      id: 4,
      name: 'Öntőhíd',
      poly: box(-2, 14, 2, 30),
      floorY: 0,
      ceilY: 10,
      light: 0.44,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalRibbed' },
    },
    {
      id: 5,
      name: 'Keleti medence',
      poly: box(2, 14, 14, 30),
      floorY: -1.2,
      ceilY: 10,
      light: 0.34,
      hazard: 7,
      tex: { floor: 'nukage', ceil: 'ceilingTech', wall: 'metalPlate' },
    },

    // --- Öntőcsarnok és a fölötte futó galéria ------------------------------
    {
      id: 6,
      name: 'Öntőcsarnok',
      poly: box(-16, 30, 16, 50),
      floorY: 0,
      ceilY: 11,
      light: 0.42,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },
    {
      id: 7,
      name: 'Teherlift',
      poly: box(16, 30, 20, 32),
      floorY: 0,
      ceilY: 11,
      light: 0.4,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
      lift: { lowY: 0, highY: 4.5, speed: 2.2, waitTime: 4, startAt: 'low', autoTrigger: true },
    },
    {
      id: 8,
      name: 'Kezelőgaléria',
      poly: box(16, 32, 28, 46),
      floorY: 4.5,
      ceilY: 11,
      light: 0.38,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
    },
    {
      id: 9,
      name: 'Őrposzt',
      poly: box(28, 38, 34, 44),
      floorY: 4.5,
      ceilY: 9,
      light: 0.3,
      tex: { floor: 'floorTile', ceil: 'metalRibbed', wall: 'techPanel' },
    },

    // --- Átvezetés a reaktorhoz ---------------------------------------------
    {
      id: 10,
      name: 'Zsilipfolyosó',
      poly: box(-4, 50, 4, 58),
      floorY: 0,
      ceilY: 4,
      light: 0.32,
      tex: { floor: 'floorTile', ceil: 'metalRibbed', wall: 'concrete' },
    },
    {
      id: 11,
      name: 'Ülepítő',
      poly: box(-18, 50, -12, 58),
      floorY: -0.4,
      ceilY: 8,
      light: 0.28,
      hazard: 5,
      tex: { floor: 'nukage', ceil: 'ceilingTech', wall: 'concrete' },
    },

    // --- A záró aréna --------------------------------------------------------
    {
      id: 12,
      name: 'Reaktorterem',
      poly: box(-18, 58, 18, 82),
      floorY: 0,
      ceilY: 14,
      light: 0.36,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },
    {
      id: 13,
      name: 'Szerszámkamra',
      poly: box(18, 60, 26, 68),
      floorY: 0,
      ceilY: 6,
      light: 0.26,
      secret: true,
      tex: { floor: 'floorTile', ceil: 'metalRibbed', wall: 'techPanel' },
    },
    {
      id: 14,
      name: 'Magkamra',
      poly: box(-5, 82, 5, 92),
      floorY: 0.4,
      ceilY: 10,
      light: 0.62,
      tex: { floor: 'floorTile', ceil: 'lightPanel', wall: 'techPanel' },
    },
  ],

  portals: [
    { id: 1, rooms: [1, 2], p0: v(-2, 4), p1: v(2, 4) },
    { id: 2, rooms: [2, 4], p0: v(-2, 14), p1: v(2, 14) },
    // A híd pereme: le lehet esni a savba, vissza csak a ládákon.
    { id: 3, rooms: [4, 3], p0: v(-2, 14), p1: v(-2, 30) },
    { id: 4, rooms: [4, 5], p0: v(2, 14), p1: v(2, 30) },
    { id: 5, rooms: [4, 6], p0: v(-2, 30), p1: v(2, 30) },
    { id: 6, rooms: [3, 6], p0: v(-14, 30), p1: v(-2, 30) },
    { id: 7, rooms: [5, 6], p0: v(2, 30), p1: v(14, 30) },
    { id: 8, rooms: [6, 7], p0: v(16, 30), p1: v(16, 32) },
    // A galéria csak a felső liftállásból érhető el.
    { id: 9, rooms: [7, 8], p0: v(16, 32), p1: v(20, 32) },
    // A galéria pereme: fentről belátni és lelőni a csarnokot, lefelé ugorható.
    { id: 10, rooms: [6, 8], p0: v(16, 32), p1: v(16, 46) },
    { id: 11, rooms: [8, 9], p0: v(28, 38), p1: v(28, 44) },
    { id: 12, rooms: [6, 10], p0: v(-4, 50), p1: v(4, 50) },
    { id: 13, rooms: [6, 11], p0: v(-16, 50), p1: v(-12, 50) },
    { id: 14, rooms: [11, 12], p0: v(-18, 58), p1: v(-12, 58) },
    {
      id: 15,
      rooms: [10, 12],
      p0: v(-4, 58),
      p1: v(4, 58),
      door: { kind: 'door', speed: 2.4, waitTime: 0, key: 'blue', tex: 'doorTech' },
    },
    { id: 16, rooms: [12, 13], p0: v(18, 60), p1: v(18, 68) },
    { id: 17, rooms: [12, 14], p0: v(-5, 82), p1: v(5, 82) },
  ],

  props: [
    // --- Hídkorlát -----------------------------------------------------------
    // ALACSONY (0.55), szándékosan. A korábbi 0.95-ös korlát fölött a szem
    // (1.62) még ellátott, a LEFELÉ irányuló lövés viszont a korlátba
    // csapódott: a játékos látta a medencében álló ellenfelet, de nem tudta
    // eltalálni. Ez a legrosszabb fajta hiba — úgy néz ki, mintha a fegyver
    // nem működne. Ennél a magasságnál a lövés is átmegy felette.
    { id: 1, kind: 'box', pos: v(-1.9, 22), size: v(0.12, 8), bottom: 0, top: 0.55, tex: 'metalRibbed', solid: true },
    { id: 2, kind: 'box', pos: v(1.9, 22), size: v(0.12, 8), bottom: 0, top: 0.55, tex: 'metalRibbed', solid: true },

    // --- Kimászó lépcső a medencékből ---------------------------------------
    //
    // HÁROM fok, egyenként 0.35 egység — a 0.45-ös automatikus fellépés
    // alatt, biztonságos ráhagyással. A korábbi két láda pontosan 0.45-ös
    // fokokat adott, ami a lépcsőzés HATÁRÁN volt: elég volt egy hajszálnyi
    // pozíciókülönbség, és a játékos bent ragadt a savban. Beragadni egy
    // sebző térben a lehető legrosszabb hiba — csak újraindítás segít.
    // A lépcső mindkét medencében a hídhoz simul, hogy magától megtaláljuk.
    { id: 3, kind: 'box', pos: v(-2.9, 26), size: v(0.75, 1.3), bottom: -1.2, top: -0.15, tex: 'metalPlate', solid: true },
    { id: 4, kind: 'box', pos: v(-4.3, 26), size: v(0.7, 1.3), bottom: -1.2, top: -0.5, tex: 'metalPlate', solid: true },
    { id: 5, kind: 'box', pos: v(-5.7, 26), size: v(0.7, 1.3), bottom: -1.2, top: -0.85, tex: 'metalPlate', solid: true },
    { id: 6, kind: 'box', pos: v(2.9, 18), size: v(0.75, 1.3), bottom: -1.2, top: -0.15, tex: 'metalPlate', solid: true },
    { id: 20, kind: 'box', pos: v(4.3, 18), size: v(0.7, 1.3), bottom: -1.2, top: -0.5, tex: 'metalPlate', solid: true },
    { id: 21, kind: 'box', pos: v(5.7, 18), size: v(0.7, 1.3), bottom: -1.2, top: -0.85, tex: 'metalPlate', solid: true },

    // --- Öntőüstök a medencék fölött (dekor + fedezék) ---------------------
    { id: 7, kind: 'cylinder', pos: v(-9, 20), size: v(1.6, 1.6), bottom: 4.5, top: 7.5, tex: 'metalPlate', solid: false, emissive: 0.3, emissiveColor: 0xff6a1f },
    { id: 8, kind: 'cylinder', pos: v(8, 25), size: v(1.6, 1.6), bottom: 4.5, top: 7.5, tex: 'metalPlate', solid: false, emissive: 0.3, emissiveColor: 0xff6a1f },
    { id: 9, kind: 'cylinder', pos: v(-9, 26), size: v(0.5, 0.5), bottom: 0, top: 10, tex: 'metalRibbed', solid: true },
    { id: 10, kind: 'cylinder', pos: v(8, 17), size: v(0.5, 0.5), bottom: 0, top: 10, tex: 'metalRibbed', solid: true },

    // --- Öntőcsarnok: oszlopsor és ládafedezék ------------------------------
    { id: 11, kind: 'box', pos: v(-10, 36), size: v(1.2, 1.2), bottom: 0, top: 11, tex: 'brick', solid: true },
    { id: 12, kind: 'box', pos: v(10, 36), size: v(1.2, 1.2), bottom: 0, top: 11, tex: 'brick', solid: true },
    { id: 13, kind: 'box', pos: v(-10, 45), size: v(1.2, 1.2), bottom: 0, top: 11, tex: 'brick', solid: true },
    { id: 14, kind: 'box', pos: v(10, 45), size: v(1.2, 1.2), bottom: 0, top: 11, tex: 'brick', solid: true },
    { id: 15, kind: 'box', pos: v(0, 40), size: v(2.4, 1.6), bottom: 0, top: 1.3, tex: 'metalPlate', solid: true },
    { id: 16, kind: 'box', pos: v(-5, 33), size: v(1.3, 1.3), bottom: 0, top: 1.0, tex: 'metalPlate', solid: true },
    { id: 17, kind: 'box', pos: v(5.5, 47), size: v(1.3, 1.3), bottom: 0, top: 1.0, tex: 'metalPlate', solid: true },
    { id: 18, kind: 'box', pos: v(15.85, 39), size: v(0.15, 7), bottom: 4.5, top: 5.6, tex: 'metalRibbed', solid: true },

    // --- Világító panelek ---------------------------------------------------
    { id: 19, kind: 'box', pos: v(-15.7, 35), size: v(0.15, 2.6), bottom: 6, top: 7.2, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 20, kind: 'box', pos: v(-15.7, 45), size: v(0.15, 2.6), bottom: 6, top: 7.2, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 21, kind: 'box', pos: v(-17.7, 70), size: v(0.15, 3), bottom: 7, top: 8.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 22, kind: 'box', pos: v(17.7, 70), size: v(0.15, 3), bottom: 7, top: 8.4, tex: 'lightPanel', solid: false, emissive: 1 },

    // --- Reaktorterem: a mag és a körülötte lévő fedezékek ------------------
    { id: 23, kind: 'cylinder', pos: v(0, 70), size: v(3.2, 3.2), bottom: 0, top: 9, tex: 'techPanel', solid: true },
    { id: 24, kind: 'cylinder', pos: v(0, 70), size: v(3.6, 3.6), bottom: 2.4, top: 3.2, tex: 'hazardStripe', solid: true },
    { id: 25, kind: 'cylinder', pos: v(-11, 63), size: v(1.1, 1.1), bottom: 0, top: 14, tex: 'metalRibbed', solid: true },
    { id: 26, kind: 'cylinder', pos: v(11, 63), size: v(1.1, 1.1), bottom: 0, top: 14, tex: 'metalRibbed', solid: true },
    { id: 27, kind: 'cylinder', pos: v(-11, 77), size: v(1.1, 1.1), bottom: 0, top: 14, tex: 'metalRibbed', solid: true },
    { id: 28, kind: 'cylinder', pos: v(11, 77), size: v(1.1, 1.1), bottom: 0, top: 14, tex: 'metalRibbed', solid: true },
    { id: 29, kind: 'box', pos: v(-14, 70), size: v(1.6, 2.2), bottom: 0, top: 1.2, tex: 'metalPlate', solid: true },
    { id: 30, kind: 'box', pos: v(14, 70), size: v(1.6, 2.2), bottom: 0, top: 1.2, tex: 'metalPlate', solid: true },

    // --- Magkamra: emelvény a kijárattal ------------------------------------
    { id: 31, kind: 'box', pos: v(0, 89), size: v(2.6, 0.35), bottom: 0.4, top: 3.2, tex: 'hazardStripe', solid: false },

    // --- Belépő zsilip berendezése ------------------------------------------
    { id: 32, kind: 'cylinder', pos: v(-6, -4), size: v(0.7, 0.7), bottom: 0, top: 2.6, tex: 'metalRibbed', solid: true },
    { id: 33, kind: 'cylinder', pos: v(6, -4), size: v(0.7, 0.7), bottom: 0, top: 2.6, tex: 'metalRibbed', solid: true },
    { id: 34, kind: 'box', pos: v(0, 3.4), size: v(2.6, 0.3), bottom: 0, top: 0.35, tex: 'hazardStripe', solid: false },
  ],

  lights: [
    { pos: v(0, -2), y: 5, color: 0xffd98a, intensity: 11, distance: 17, style: 'steady' },
    { pos: v(0, 9), y: 3.2, color: 0x64c4cf, intensity: 7, distance: 12, style: 'flicker' },

    // A savas medence saját, alulról jövő fénye — ez adja a hely karakterét.
    { pos: v(-8, 22), y: 0.6, color: 0x8ad63a, intensity: 9, distance: 18, style: 'pulse' },
    { pos: v(8, 22), y: 0.6, color: 0x8ad63a, intensity: 9, distance: 18, style: 'pulse' },
    { pos: v(-9, 20), y: 5.5, color: 0xff6a1f, intensity: 10, distance: 14, style: 'flicker' },
    { pos: v(8, 25), y: 5.5, color: 0xff6a1f, intensity: 10, distance: 14, style: 'flicker' },
    { pos: v(0, 22), y: 6, color: 0xffd98a, intensity: 8, distance: 13, style: 'steady' },

    { pos: v(-8, 36), y: 8, color: 0xe4b02c, intensity: 13, distance: 22, style: 'steady' },
    { pos: v(8, 45), y: 8, color: 0xe4b02c, intensity: 12, distance: 22, style: 'flicker' },
    { pos: v(-15, 40), y: 6.6, color: 0xffd98a, intensity: 9, distance: 14, style: 'pulse' },
    { pos: v(22, 38), y: 8, color: 0x64c4cf, intensity: 10, distance: 18, style: 'steady' },
    { pos: v(31, 41), y: 7, color: 0x64c4cf, intensity: 8, distance: 12, style: 'flicker' },

    { pos: v(0, 54), y: 3.4, color: 0xffd98a, intensity: 7, distance: 11, style: 'steady' },
    { pos: v(-15, 54), y: 4, color: 0x8ad63a, intensity: 7, distance: 13, style: 'pulse' },

    { pos: v(0, 70), y: 9.5, color: 0x8affff, intensity: 15, distance: 26, style: 'pulse' },
    { pos: v(-12, 62), y: 8, color: 0xe4b02c, intensity: 10, distance: 18, style: 'steady' },
    { pos: v(12, 78), y: 8, color: 0xe4b02c, intensity: 10, distance: 18, style: 'steady' },
    { pos: v(22, 64), y: 5, color: 0x8affff, intensity: 7, distance: 11, style: 'pulse' },
    { pos: v(0, 87), y: 8, color: 0xffffff, intensity: 14, distance: 16, style: 'steady' },
  ],

  things: [
    { kind: 'playerStart', type: 'player', pos: v(0, -2), yaw: 0 },

    // === ENCOUNTEREK — gazda: X ==========================================
    // GDD 4. pont, 2. pálya: 31 ellenfél (12 grunt, 9 imp, 7 hound, 3 sentinel).
    // C csak a térnek megfelelő kiinduló elrendezést adta meg; az arányokat,
    // a csoportosítást és az ébresztési sorrendet X hangolja.

    // Bejárat: 4 grunt + 2 imp — a folyosó után az első nyílt tér.
    // A sav alján nincs lövész: a híd korlátja mögül onnan igazságtalanul
    // nehéz visszalőni. A lövészek a hídon vannak, a medence houndjai feljönnek.
    // Az első két őrszem hat egységgel a híd eleje után áll. Így a
    // szervizfolyosóból kilépő játékos először a savat és a keskeny hidat
    // olvassa, és csak utána indul a 0,75 + 0,35 mp-es első lövésablak.
    { kind: 'enemy', type: 'grunt', pos: v(-1.1, 20), yaw: 1.6, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(1.1, 21), yaw: -1.6, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(-1.1, 27), yaw: 1.6, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(1.1, 28), yaw: -1.6, group: 'a' },
    { kind: 'enemy', type: 'imp', pos: v(-1.05, 24), yaw: 1.6, group: 'a' },
    { kind: 'enemy', type: 'imp', pos: v(1.05, 26), yaw: -1.6, group: 'a' },

    // Hídharc: 3 hound szemből, a keskeny hídon.
    { kind: 'enemy', type: 'hound', pos: v(-7, 20), yaw: 0, group: 'b' },
    { kind: 'enemy', type: 'hound', pos: v(8, 24), yaw: 0, group: 'b' },
    { kind: 'enemy', type: 'hound', pos: v(0, 28), yaw: 0, group: 'b' },

    // Nagy csarnok: 4 grunt + 2 imp + 1 sentinel — az első célpriorizálási vizsga.
    { kind: 'enemy', type: 'grunt', pos: v(-12, 34), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(12, 34), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(-8, 47), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'grunt', pos: v(8, 48), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(-3, 44), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'imp', pos: v(4, 43), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'sentinel', pos: v(0, 48.5), yaw: Math.PI, group: 'c' },

    // Őrposzt a galérián: 2 sentinel, magasból.
    { kind: 'enemy', type: 'sentinel', pos: v(30, 39.5), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'sentinel', pos: v(32, 42.5), yaw: Math.PI, group: 'd' },

    // Reaktor-ostrom: a pálya csúcsa — 3 hound + 3 imp + kísérők.
    { kind: 'enemy', type: 'hound', pos: v(-8, 64), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(8, 64), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(0, 78), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(-14, 67), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(14, 67), yaw: Math.PI, group: 'e' },
    { kind: 'enemy', type: 'imp', pos: v(0, 80), yaw: 0, group: 'e' },

    // Kijárati őrség.
    { kind: 'enemy', type: 'grunt', pos: v(-3, 86), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(3, 86), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(-3, 89), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'grunt', pos: v(3, 89), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'imp', pos: v(-3, 91), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'imp', pos: v(3, 91), yaw: 0, group: 'f' },
    { kind: 'enemy', type: 'hound', pos: v(0, 83.5), yaw: 0, group: 'f' },

    // === LŐSZER ÉS ÉLET (GDD 3. pont, 2. pálya) ==========================
    { kind: 'pickup', type: 'bullets', pos: v(-5, 1) },
    { kind: 'pickup', type: 'shells', pos: v(5, 1) },

    // A medence alján a jutalom — ezért éri meg leugrani a savba.
    { kind: 'pickup', type: 'shells', pos: v(9, 27) },

    { kind: 'pickup', type: 'bullets', pos: v(-13, 32) },
    { kind: 'pickup', type: 'bullets', pos: v(13, 48) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(0, 43) },

    // A galéria a „pihenő": nagy medkit + rakétavető, 15 mp harc nélkül.
    { kind: 'pickup', type: 'weapon:chaingun', pos: v(20, 36) },
    { kind: 'pickup', type: 'medkitLarge', pos: v(24, 44) },
    { kind: 'pickup', type: 'bullets', pos: v(26, 34) },
    { kind: 'pickup', type: 'bullets', pos: v(6, 34) },
    { kind: 'pickup', type: 'bullets', pos: v(2, 46) },
    { kind: 'pickup', type: 'armor', pos: v(18, 45) },

    // Az őrposzt tartja a kék kulcsot.
    { kind: 'pickup', type: 'keyBlue', pos: v(31, 41) },

    // Titkos szerszámkamra.
    { kind: 'pickup', type: 'weapon:rocket', pos: v(22, 64) },
    { kind: 'pickup', type: 'rockets', pos: v(24, 66) },

    // Reaktorterem.
    { kind: 'pickup', type: 'bullets', pos: v(-16, 60) },
    { kind: 'pickup', type: 'shells', pos: v(16, 60) },
    { kind: 'pickup', type: 'cells', pos: v(-15, 79) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(15, 79) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(0, 84) },

    { kind: 'exit', type: 'exit', pos: v(0, 90) },
  ],
}
